import React, { useState, useEffect } from 'react';

import { FolderIcon } from '@vlabs/icons';
import { optionPropType } from '@vlabs/shared';
import {
  SourceSelectPopover,
  HandlersSelectPopover,
  TagsSelectPopover,
  LabelInputPopover,
  SimilarityInputsPopover,
  LivenessCheckboxesPopover,
  DeepfakeCheckboxesPopover,
  EmotionsCheckboxesPopover,
  MasksCheckboxesPopover,
  AgeCheckboxesPopover,
  GenderCheckboxesPopover,
  ApparentAgeCheckboxesPopover,
  ApparentGenderCheckboxesPopover,
  LowerCheckboxesPopover,
  UpperCheckboxesPopover,
  HeadwearCheckboxesPopover,
  ShoesColorsCheckboxesPopover,
  BackpackCheckboxesPopover,
  GeolocationFilter,
} from '@vlabs/shared/components/filters-panel';
import {
  Control, SideMenu, Group, useWindowSize, Badge,
} from '@vlabs/uikit';
import PropTypes from 'prop-types';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';

import { selectHandlerOptions } from '@vlabs/pages/handlers/selectors';

import { defaultFilters } from '@vlabs/pages/events/defaultFilters';

import st from './FilterForm.module.sass';
import { setFilterCounts } from '../latest-events/store';

function FilterForm({
  handlerOptions = [],
  setTotalCount = undefined,
}) {
  const { t } = useTranslation();
  const { reset, setFilters } = useFormContext();
  const [counts, setCounts] = useState({});
  const handleCountChange = (key, count) => {
    setCounts((prev) => {
      if (prev[key] === count) return prev;
      return { ...prev, [key]: count };
    });
  };
  const sumCounts = (keys) => keys.reduce((acc, key) => acc + (counts[key] || 0), 0);

  const filterCountCommon = sumCounts([
    'sources', 'stream_ids', 'handlers', 'tags', 'location',
  ]);

  const filterCountComparison = sumCounts([
    'top_matching_candidates_label', 'similarity',
  ]);

  const filterCountFace = sumCounts([
    'gender', 'age', 'deepfake', 'liveness', 'emotions', 'masks',
  ]);

  const filterCountBody = sumCounts([
    'apparent_gender', 'apparent_age',
    'lower', 'upper', 'headwear', 'shoes', 'backpack',
  ]);

  useEffect(() => {
    const totalCount = Object.values(counts).reduce((acc, n) => acc + (n || 0), 0);
    setTotalCount(totalCount);
  }, [counts, setTotalCount]);

  const resetForm = () => {
    reset(defaultFilters);
    setFilters({});
  };
  const windowSize = useWindowSize();
  const isCompact = windowSize.width < 1920;

  return (
    <div
      className={st.FilterForm}
      data-testid="filtersPanel"
    >

      <Group
        gapSize={0}
        vertical
      >

        <SideMenu.MenuItem
          data-testid="events.commonFilters"
          leftIcon={<FolderIcon />}
          rightIcon={filterCountCommon > 0 && <Badge value={filterCountCommon} />}
          value={t('general')}
        >
          <SourceSelectPopover onCountChange={handleCountChange} />
          <HandlersSelectPopover
            handlerOptions={handlerOptions}
            onCountChange={handleCountChange}
          />
          <GeolocationFilter onCountChange={handleCountChange} />
          <TagsSelectPopover onCountChange={handleCountChange} />
        </SideMenu.MenuItem>

        <div className={st.Divider} />

        <SideMenu.MenuItem
          data-testid="events.comparisonFilters"
          leftIcon={<FolderIcon />}
          rightIcon={filterCountComparison > 0 && <Badge value={filterCountComparison} />}
          value={t('labels')}
        >
          <LabelInputPopover onCountChange={handleCountChange} />
          <SimilarityInputsPopover onCountChange={handleCountChange} />
        </SideMenu.MenuItem>

        <div className={st.Divider} />

        <SideMenu.MenuItem
          data-testid="events.faceAttributesFilters"
          leftIcon={<FolderIcon />}
          rightIcon={filterCountFace > 0 && <Badge value={filterCountFace} />}
          value={t('events:filter form.face attributes and properties')}
        >
          <GenderCheckboxesPopover onCountChange={handleCountChange} />
          <AgeCheckboxesPopover onCountChange={handleCountChange} />
          <EmotionsCheckboxesPopover onCountChange={handleCountChange} />
          <MasksCheckboxesPopover onCountChange={handleCountChange} />
          <LivenessCheckboxesPopover onCountChange={handleCountChange} />
          <DeepfakeCheckboxesPopover onCountChange={handleCountChange} />
        </SideMenu.MenuItem>

        <div className={st.Divider} />

        <SideMenu.MenuItem
          data-testid="events.bodyAttributesFilters"
          leftIcon={<FolderIcon />}
          rightIcon={filterCountBody > 0 && <Badge value={filterCountBody} />}
          value={t('events:filter form.body attributes and properties')}
        >
          <ApparentAgeCheckboxesPopover onCountChange={handleCountChange} />
          <ApparentGenderCheckboxesPopover onCountChange={handleCountChange} />
          <LowerCheckboxesPopover onCountChange={handleCountChange} />
          <UpperCheckboxesPopover onCountChange={handleCountChange} />
          <HeadwearCheckboxesPopover onCountChange={handleCountChange} />
          <ShoesColorsCheckboxesPopover onCountChange={handleCountChange} />
          <BackpackCheckboxesPopover onCountChange={handleCountChange} />
        </SideMenu.MenuItem>

        <div className={st.Divider} />
      </Group>

      <div className={st.ButtonPanel}>
        <Control.Button
          className={st.Button}
          data-testid="FilterForm.clearButton"
          kind="negative"
          onClick={resetForm}
          size={isCompact ? 'small' : 'medium'}
        >
          {t('button.clear')}
        </Control.Button>
      </div>
    </div>
  );
}

FilterForm.propTypes = {
  handlerOptions: PropTypes.arrayOf(optionPropType),
  setTotalCount: PropTypes.func,
};

export default connect(
  (state) => ({
    handlerOptions: selectHandlerOptions(state),
  }),
  (dispatch) => ({
    setTotalCount: (count) => dispatch(setFilterCounts(count)),
  }),
)(FilterForm);
